import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/de';

import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField,
    Select, MenuItem, InputLabel, FormControl, Typography, Box, ToggleButton,
    ToggleButtonGroup, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { API_BASE_URL } from '../../../App';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Berlin");
dayjs.locale('de'); // Setzt die deutsche Lokalisierung für Day.js

// Initialer leerer State für das Formular
const initialFormData = {
    name: '', type: 'Mannschaftsspiel', startDate: '', endDate: '', startTime: '',
    endTime: '', courts: [], weekdays: [], team: '', coach: '',
    opponent: '', weekly: 1,
};

const timeOptions = [];
for (let h = 8; h < 24; h++) {
    timeOptions.push(`${String(h).padStart(2, '0')}:00`);
}
const endTimeOptions = [...timeOptions.filter(t => t !== '08:00'), '00:00'];

const EventModal = ({ isOpen, onClose, eventToEdit }) => {
    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [conflicts, setConflicts] = useState([]); // State für Konflikte
    const [availableCourts, setAvailableCourts] = useState([]); // State für verfügbare Plätze

    // --- Dynamische Logik mit useEffect ---

    // Destructure needed values to genericize dependencies and avoid lint warnings
    const { type, team, opponent, startDate } = formData;

    useEffect(() => {
        setConflicts([]); // Reset conflicts when modal opens or event changes
        if (eventToEdit) {
            setFormData({
                name: eventToEdit.name || '',
                type: eventToEdit.type || '', 
                startDate: dayjs(eventToEdit.startDate).tz().format('YYYY-MM-DD'),
                endDate: dayjs(eventToEdit.endDate).tz().format('YYYY-MM-DD'),
                startTime: dayjs(eventToEdit.startTime).tz().format('HH:mm'),
                endTime: dayjs(eventToEdit.endTime).tz().format('HH:mm'),
                courts: eventToEdit.courts || [],
                weekdays: eventToEdit.weekdays || [],
                team: eventToEdit.team || '',
                coach: eventToEdit.coach || '',
                opponent: eventToEdit.opponent || '',
                weekly: eventToEdit.weekly !== undefined ? eventToEdit.weekly : 1
            });
        } else {
            setFormData(initialFormData);
        }
    }, [eventToEdit, isOpen]); 

    useEffect(() => {
        if (isOpen) {
            const fetchCourts = async () => {
                try {
                    const response = await axios.get(`${API_BASE_URL}/api/courts`);
                    setAvailableCourts(response.data);
                } catch (error) {
                    console.error("Error fetching courts:", error);
                    toast.error("Fehler beim Laden der Plätze");
                }
            };
            fetchCourts();
        }
    }, [isOpen]);

    
    useEffect(() => {
        let newName = '';
        if (type === 'Training' && team) {
            newName = `Training ${team}`;
        } else if (type === 'Mannschaftsspiel' && team && opponent) {
            newName = `${team} gegen ${opponent}`;
        } else if (type === 'Jugend') {
            newName = 'Jugendtraining';
        } else if (type === 'Platzsperre') {
            newName = 'Platzsperre';
        } else if (type === 'Turnier') {
            newName = 'Turnier';
        }
        
        // Only update if needed to avoid unnecessary renders
        setFormData(prev => {
            if (prev.name === newName) return prev;
            return { ...prev, name: newName };
        });
    }, [type, team, opponent]);

    useEffect(() => {
        if (['Mannschaftsspiel', 'Turnier', 'Platzsperre'].includes(type)) {
            setFormData(prev => ({ ...prev, endDate: prev.startDate }));
        }
    }, [startDate, type]);

    // --- Validierung & Handler ---

    const validate = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = 'Name ist erforderlich.';
        if (!formData.type) newErrors.type = 'Typ ist erforderlich.';
        if (!formData.startDate) newErrors.startDate = 'Startdatum ist erforderlich.';
        if (!formData.endDate) newErrors.endDate = 'Enddatum ist erforderlich.';
        if (!formData.startTime) newErrors.startTime = 'Startzeit ist erforderlich.';
        if (!formData.endTime) newErrors.endTime = 'Endzeit ist erforderlich.';

        if (formData.startDate && (dayjs(formData.startDate).isBefore(minDateLimit) || dayjs(formData.startDate).isAfter(maxDateLimit))) {
            newErrors.startDate = 'Datum liegt außerhalb des erlaubten Zeitraums.';
        }
        if (formData.endDate && (dayjs(formData.endDate).isBefore(minDateLimit) || dayjs(formData.endDate).isAfter(maxDateLimit))) {
            newErrors.endDate = 'Datum liegt außerhalb des erlaubten Zeitraums.';
        }

        if (formData.startDate && formData.endDate && dayjs(formData.endDate).isBefore(formData.startDate)) {
            newErrors.endDate = 'Enddatum muss nach dem Startdatum liegen.';
        }
        
        // Time validation: Allow 00:00 as end time (next day/midnight)
        if (formData.startTime && formData.endTime) {
            const isMidnightEnd = formData.endTime === '00:00';
            if (!isMidnightEnd && formData.endTime <= formData.startTime && dayjs(formData.startDate).isSame(formData.endDate, 'day')) {
                newErrors.endTime = 'Endzeit muss nach der Startzeit liegen.';
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleTypeChange = (e) => {
         const newType = e.target.value;
         // Reset data but keep type, only if NOT editing or user explicitly changes type.
         setFormData({ ...initialFormData, type: newType });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handleToggleChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e, overwrite = false) => {
        if (e) e.preventDefault();
        if (!validate()) {
            toast.warn('Bitte korrigieren Sie die Fehler im Formular.');
            return;
        }
        setLoading(true);
        setConflicts([]); // Clear previous conflicts

        const apiPayload = {
            ...formData,
            // Backend handle HH:mm strings and YYYY-MM-DD dates robustly now
            startTime: formData.startTime, 
            endTime: formData.endTime,
            startDate: formData.startDate,
            endDate: formData.endDate,
            weekly: ['Mannschaftsspiel', 'Turnier', 'Platzsperre'].includes(formData.type) ? 0 : formData.weekly,
            overwriteConflicts: overwrite
        };

        try {
            if (eventToEdit) {
                 const response = await axios.put(`${API_BASE_URL}/api/events/${eventToEdit._id}`, apiPayload);
                 toast.success(response.data.message || 'Termin erfolgreich aktualisiert!');
            } else {
                 const response = await axios.post(`${API_BASE_URL}/api/events/create`, apiPayload);
                 toast.success(response.data.message || 'Termin erfolgreich erstellt!');
            }
            handleClose();
        } catch (error) {
            if (error.response && error.response.status === 409) {
                // Conflicts found
                setConflicts(error.response.data.conflicts || []);
                toast.warning('Konflikte gefunden! Bitte überprüfen.');
            } else {
                toast.error(error.response?.data?.message || 'Ein Fehler ist aufgetreten.');
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleOverwrite = () => {
        handleSubmit(null, true);
    };

    const handleClose = () => {
        setFormData(initialFormData);
        setErrors({});
        setConflicts([]);
        onClose();
    };
    
    // --- Helper for formatting conflict dates ---
    const formatConflictDate = (dateString) => {
        return dayjs(dateString).tz().format('DD.MM.YYYY HH:mm');
    };

    // --- Definitionen für die Anzeige (unverändert) ---
    const weekdayOptions = [{ label: 'Mo', value: 1 }, { label: 'Di', value: 2 }, { label: 'Mi', value: 3 }, { label: 'Do', value: 4 }, { label: 'Fr', value: 5 }, { label: 'Sa', value: 6 }, { label: 'So', value: 0 }];
    const isTypeSelected = formData.type !== '';
    const showTeamField = ['Training', 'Mannschaftsspiel'].includes(formData.type);
    const showOpponentField = formData.type === 'Mannschaftsspiel';
    const showCoachField = formData.type === 'Jugend';
    const showWeekdays = !['Mannschaftsspiel', 'Turnier', 'Platzsperre'].includes(formData.type);
    const showEndDate = !['Mannschaftsspiel', 'Turnier', 'Platzsperre'].includes(formData.type);
    const isNameDisabled = ['Training', 'Mannschaftsspiel', 'Jugend', 'Platzsperre'].includes(formData.type);

    const minDateLimit = dayjs.tz().subtract(3, 'month').format('YYYY-MM-DD');
    const maxDateLimit = dayjs.tz().add(1, 'year').format('YYYY-MM-DD');

    return (
        <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle fontWeight="bold">
                {eventToEdit ? 'Termin bearbeiten' : 'Neuen Termin erstellen'}
                <IconButton aria-label="close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <form onSubmit={(e) => handleSubmit(e, false)}>
                <DialogContent>
                    
                    {/* Conflict Alert Section */}
                    {conflicts.length > 0 && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#fff4e5', border: '1px solid #ffcc80', borderRadius: 1 }}>
                            <Typography variant="subtitle1" color="warning.main" fontWeight="bold" gutterBottom>
                                {conflicts.length} Konflikte mit bestehenden Buchungen gefunden:
                            </Typography>
                            <ul style={{ margin: 0, paddingLeft: '20px', maxHeight: '150px', overflowY: 'auto' }}>
                                {conflicts.map((c, index) => {
                                    let conflictName = 'Unbekannt/Gast';
                                    if (c.eventId && c.eventId.name) {
                                        conflictName = `Termin: ${c.eventId.name}`;
                                    } else if (c.playerSnapshot) {
                                        conflictName = c.playerSnapshot;
                                    } else if (c.mainPlayer) {
                                        conflictName = `${c.mainPlayer.firstName} ${c.mainPlayer.lastName}`;
                                    } else if (c.playerId) {
                                         conflictName = `${c.playerId.firstName} ${c.playerId.lastName}`;
                                    } else if (c.partnersSnapshot && c.partnersSnapshot.length > 0) {
                                        conflictName = `Gast: ${c.partnersSnapshot[0]}`;
                                    }

                                    return (
                                        <li key={index} style={{ marginBottom: '4px', fontSize: '0.9rem' }}>
                                            <strong>{formatConflictDate(c.startTime)}</strong> - Platz {c.courtId} 
                                            {` (${conflictName})`}
                                        </li>
                                    );
                                })}
                            </ul>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Möchtest du diese Buchungen löschen und den Termin trotzdem erstellen?
                            </Typography>
                            <Button 
                                variant="contained" 
                                color="error" 
                                size="small" 
                                sx={{ mt: 1 }}
                                onClick={handleOverwrite}
                            >
                                Trotzdem erstellen (Konflikte löschen)
                            </Button>
                        </Box>
                    )}

                    <FormControl fullWidth margin="normal">
                        <InputLabel id="event-type-label">Kategorie</InputLabel>
                        <Select labelId="event-type-label" name="type" value={formData.type} label="Kategorie" onChange={handleTypeChange} size="small">
                            <MenuItem value="Training">Training</MenuItem>
                            <MenuItem value="Mannschaftsspiel">Mannschaftsspiel</MenuItem>
                            <MenuItem value="Turnier">Turnier</MenuItem>
                            <MenuItem value="Jugend">Jugend</MenuItem>
                            <MenuItem value="Platzsperre">Platzsperre</MenuItem>
                        </Select>
                    </FormControl>
                    {isTypeSelected && (
                        <Grid container spacing={2} mt={0}>
                            <Grid item xs={12}><TextField name="name" label="Bezeichnung" value={formData.name} onChange={handleChange} fullWidth required size="small" disabled={isNameDisabled} /></Grid>
                            {showTeamField && <Grid item xs={12} md={showOpponentField ? 6 : 12}><TextField name="team" label="Team" value={formData.team} onChange={handleChange} fullWidth required size="small" /></Grid>}
                            {showOpponentField && <Grid item xs={12} md={6}><TextField name="opponent" label="Gegner" value={formData.opponent} onChange={handleChange} fullWidth required size="small" /></Grid>}
                            {showCoachField && <Grid item xs={12}><TextField name="coach" label="Trainer" value={formData.coach} onChange={handleChange} fullWidth required size="small" /></Grid>}
                            <Grid item xs={12} mt={1}><Typography variant="subtitle1" component="h3" fontWeight="500">Zeitliche Planung</Typography></Grid>
                            <Grid item xs={12} md={showEndDate ? 6 : 12}>
                                <DatePicker
                                    label="Startdatum"
                                    value={formData.startDate ? dayjs(formData.startDate) : null}
                                    onChange={(newValue) => setFormData(prev => ({ ...prev, startDate: newValue ? newValue.format('YYYY-MM-DD') : '' }))}
                                    minDate={dayjs().subtract(3, 'month')}
                                    maxDate={dayjs().add(1, 'year')}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                            required: true,
                                            error: !!errors.startDate,
                                            helperText: errors.startDate
                                        }
                                    }}
                                />
                            </Grid>
                            {showEndDate && (
                                <Grid item xs={12} md={6}>
                                    <DatePicker
                                        label="Enddatum"
                                        value={formData.endDate ? dayjs(formData.endDate) : null}
                                        onChange={(newValue) => setFormData(prev => ({ ...prev, endDate: newValue ? newValue.format('YYYY-MM-DD') : '' }))}
                                        minDate={dayjs().subtract(3, 'month')}
                                        maxDate={dayjs().add(1, 'year')}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small',
                                                required: true,
                                                error: !!errors.endDate,
                                                helperText: errors.endDate
                                            }
                                        }}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="start-time-label">Startzeit</InputLabel>
                                    <Select
                                        labelId="start-time-label"
                                        name="startTime"
                                        value={formData.startTime}
                                        label="Startzeit"
                                        onChange={handleChange}
                                    >
                                        {timeOptions.map(t => <MenuItem key={`start-${t}`} value={t}>{t}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small" error={!!errors.endTime}>
                                    <InputLabel id="end-time-label">Endzeit</InputLabel>
                                    <Select
                                        labelId="end-time-label"
                                        name="endTime"
                                        value={formData.endTime}
                                        label="Endzeit"
                                        onChange={handleChange}
                                    >
                                        {endTimeOptions.map(t => <MenuItem key={`end-${t}`} value={t}>{t}</MenuItem>)}
                                    </Select>
                                    {errors.endTime && <Typography variant="caption" color="error" sx={{ ml: 2 }}>{errors.endTime}</Typography>}
                                </FormControl>
                            </Grid>
                            {showWeekdays && (
                                <Grid item xs={12} mt={1}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={8}>
                                            <InputLabel sx={{ mb: 1, fontSize: '0.875rem' }}>Wochentage</InputLabel>
                                            <ToggleButtonGroup value={formData.weekdays} onChange={(e, val) => handleToggleChange('weekdays', val)} color="primary" fullWidth size="small">
                                                {weekdayOptions.map(day => <ToggleButton key={day.value} value={day.value} sx={{ flexGrow: 1, px: { xs: 1, sm: 2 } }}>{day.label}</ToggleButton>)}
                                            </ToggleButtonGroup>
                                        </Grid>
                                        <Grid item xs={4}>
                                             <InputLabel sx={{ mb: 1, fontSize: '0.875rem' }}>Rhythmus</InputLabel>
                                             <Select 
                                                name="weekly" 
                                                value={formData.weekly} 
                                                onChange={handleChange} 
                                                fullWidth 
                                                size="small"
                                             >
                                                <MenuItem value={1}>Wöchentlich</MenuItem>
                                                <MenuItem value={2}>14-tägig</MenuItem>
                                             </Select>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            <Grid item xs={12} mt={1}>
                                <InputLabel sx={{ mb: 1, fontSize: '0.875rem' }}>Plätze</InputLabel>
                                <ToggleButtonGroup 
                                    value={formData.courts} 
                                    onChange={(e, val) => handleToggleChange('courts', val)} 
                                    color="primary" 
                                    fullWidth 
                                    size="small" 
                                    sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'row',
                                        flexWrap: 'wrap',
                                        gap: 1
                                    }}
                                >
                                    {availableCourts.map(court => (
                                        <ToggleButton 
                                            key={court.courtNumber} 
                                            value={court.courtNumber} 
                                            sx={{ 
                                                flex: 1,
                                                minWidth: { xs: 'calc(33.33% - 8px)', sm: '80px' },
                                                borderRadius: '4px !important',
                                                border: '1px solid rgba(0, 0, 0, 0.12) !important',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {court.name || `P${court.courtNumber}`}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                {isTypeSelected && (
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={handleClose}>Abbrechen</Button>
                        <Button type="submit" variant="contained" disabled={loading}>
                            {loading ? 'Speichern...' : (eventToEdit ? 'Speichern' : 'Erstellen')}
                        </Button>
                    </DialogActions>
                )}
            </form>
        </Dialog>
    );
};

export default EventModal;
